import { useState } from "react";
import { enqueueSnackbar } from "notistack";
import { User } from "lucide-react";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import { useStudentSession } from "../context/StudentSessionContext";
import { requestProfileEdit } from "../../../services/StudentPortal/StudentProfileService";
import { handleApiError } from "../../../utils/errorHandler";
import RequestEditModal from "./components/RequestEditModal";
import { buildDetailFields, getAdmissionNumber, getClassLabel, getStudentName, getStudentPhoto } from "./profileFieldUtils";
import "./StudentProfile.css";

// Renders only its own content — StudentPortalLayout already provides the
// sidebar/header shell around every route.
const StudentProfile = () => {
  const { student, siblings, isLoading } = useStudentSession();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestEdit = async (payload) => {
    setIsSubmitting(true);
    try {
      await requestProfileEdit(payload);
      enqueueSnackbar("Your profile edit request has been submitted", { variant: "success" });
      setIsEditModalOpen(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <Loader />;

  if (!student) {
    return (
      <div className="sp-card">
        <p className="mb-0">Your profile details aren't available right now. Please try again later.</p>
      </div>
    );
  }

  const name = getStudentName(student) || "Student";
  const classLabel = getClassLabel(student);
  const admissionNumber = getAdmissionNumber(student);
  const photo = getStudentPhoto(student);
  const detailFields = buildDetailFields(student);

  // The active student is already in `siblings` on some backends — exclude
  // them so "Also enrolled" only lists the other children.
  const otherSiblings = (siblings || []).filter((sibling) => sibling?.id !== student?.id && sibling?.name);

  return (
    <div>
      <div className="sp-card sp-profile-header">
        <div className="sp-profile-identity">
          <div className="sp-profile-avatar">
            {photo ? <img src={photo} alt={name} /> : <User size={32} />}
          </div>
          <div>
            <h2 className="sp-profile-name mb-1">{name}</h2>
            <div className="sp-profile-meta">
              {classLabel && <span className="sp-profile-class-badge">{classLabel}</span>}
              {admissionNumber && <span className="sp-profile-admission">Admission No: {admissionNumber}</span>}
            </div>
          </div>
        </div>
        <Button variant="primary" onClick={() => setIsEditModalOpen(true)}>
          Request Profile Edit
        </Button>
      </div>

      {detailFields.length > 0 && (
        <div className="sp-card mt-3">
          <h3 className="sp-section-title">Profile Details</h3>
          <div className="sp-card-grid">
            {detailFields.map((field) => (
              <div key={field.label} className="sp-profile-field">
                <div className="sp-profile-field-label">{field.label}</div>
                <div className="sp-profile-field-value">{field.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {siblings?.length > 1 && otherSiblings.length > 0 && (
        <div className="sp-card mt-3">
          <h3 className="sp-section-title">Siblings</h3>
          {/* The header's ChildSwitcher already handles switching between
              children — this is informational only. */}
          <div className="sp-sibling-list">
            {otherSiblings.map((sibling) => (
              <span key={sibling.id ?? sibling.name} className="sp-sibling-chip">
                <User size={12} />
                {sibling.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <RequestEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleRequestEdit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default StudentProfile;
